import React, { useState, useEffect, useMemo, useRef } from "react";
import Modal from 'react-modal';
import cx from "classnames";
import { useDebounce, usePrevious } from "@uidotdev/usehooks";

import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";

import { bulkSaveTransactionCategories, getBulkTransactionCategories, fetchTransactions, fetchTransactionSummary, downloadTransactions, saveCategories, saveTransaction, saveRule, fetchTransactionSplit, saveTransactionSplit, deleteTransactionSplit } from "./api";
import CategoriesEditor from "./category-editor";

import Category from "./category";

import { EditRuleModal } from "./rules";
import styles from "./transactions.module.css";
import ModalLayout, { mediumModal, largeModal } from "./modal-layout";
import IndeterminateCheckbox from "./indeterminate-checkbox";
import { useAppContext } from "./app-context";
import { formatMoney } from "./utils";
import BackArrow from "./back-arrow";

const useTransactions = (filter, spaceScopedFetch) => {
    const queryClient = useQueryClient();

    const {
        data: { pages = [] } = {},
        fetchNextPage,
        fetchPreviousPage,
        hasNextPage,
        hasPreviousPage,
        isFetchingNextPage,
        isFetchingPreviousPage,
        isLoading
    } = useInfiniteQuery({
        queryKey: ['transactions', spaceScopedFetch?.ident, filter],
        queryFn: ({ pageParam }) => fetchTransactions(filter, pageParam, spaceScopedFetch),
        getNextPageParam: lastPage => lastPage.next,
        getPreviousPageParam: firstPage => firstPage.previous,
        maxPages: 3
    });

    const transactions = pages.flatMap(page => page.results);

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: [ 'transactions' ]});
    };

    useEffect(() => {
        invalidate();
    }, []);

    return [
        transactions,
        isLoading,
        (hasNextPage && !isFetchingNextPage) ? fetchNextPage : null,
        (hasPreviousPage && !isFetchingPreviousPage) ? fetchPreviousPage : null,
        invalidate
    ];
};

const formatTransactionDate = transaction => {
    return new Date(transaction.datestamp).toLocaleString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
}

const formatTransactionAmount = transaction => formatMoney(transaction.amount, transaction.currency);

const Transaction = ({
    transaction,
    checked,
    showAccountName,
    showCategories,
    accountsByUri,
    categoriesByUri,
    onClickCategories,
    onCheckedChange,
    onSplitClick
}) => {
    return (
        <tr className={styles.transaction}>
            {onCheckedChange &&
                <td>
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => onCheckedChange(e.target.checked)}
                    />
                </td>
            }
            {onSplitClick &&
                <td>
                    <button
                        className={cx("material-icons", styles.splitButton)}
                        onClick={(e) => {
                            e.stopPropagation();
                            onSplitClick(transaction);
                        }}
                        title={transaction.hasBeenSplit ? "Edit Split" : "Split Transaction"}
                    >
                        {transaction.hasBeenSplit ? "edit" : "call_split"}
                    </button>
                </td>
            }
            <td>
                {formatTransactionDate(transaction)}
            </td>
            <td><b>{transaction.description}</b></td>
            <td>{transaction.merchantName}</td>
            {showAccountName && <td>{accountsByUri[transaction.account]?.displayName}</td>}
            <td>
                {formatTransactionAmount(transaction)}
            </td>
            {showCategories &&
                <td
                    onClick={() => onClickCategories?.()}
                >
                    {transaction.categories.map(c => {
                        const category = categoriesByUri[c];
                        return (
                            category
                            ? <Category
                                category={category}
                                key={category.uri}
                                checked={true}
                            />
                            : null
                        );
                    })}
                </td>
            }
        </tr>
    );
};

const EditCategoriesModal = ({
    bulkEditButtonText,
    editingTransactions,
    commonTransactionDescription,
    defaultInterestingCategories,
    categories,
    uncategorizedCategory,
    accounts,
    onClose,
    onSave
}) => {
    const [editedCategories, setEditedCategories] = useState(null);
    const [interestingCategories, setInterestingCategories] = useState(null);

    const isOpen = !!defaultInterestingCategories;

    useEffect(() => {
        setInterestingCategories(defaultInterestingCategories);
    }, [isOpen]);

    const setInterestingCategoriesWithDefault = newInterestingCategories => {
        const excludingUncategorized = Object.entries(newInterestingCategories)
            .filter(([c, _]) => (c !== uncategorizedCategory)
        );
        const changingCategories = excludingUncategorized
            .filter(([c, _]) => newInterestingCategories[c] !== defaultInterestingCategories[c]);
        const leavingCategories = excludingUncategorized
            .filter(([c, _]) => newInterestingCategories[c] === defaultInterestingCategories[c]);
        const settingCategories = changingCategories.filter(([c, checked]) => checked === true);      
        const clearingCategories = changingCategories.filter(([c, checked]) => checked === false);

        const setUncategorized = (
            defaultInterestingCategories[uncategorizedCategory] !== true &&
            settingCategories.length === 0 && leavingCategories.length === 0 && clearingCategories.length > 0
        );
        const clearUncategorized = (
            defaultInterestingCategories[uncategorizedCategory] !== false &&
            settingCategories.length > 0
        );

        if(setUncategorized) {
            newInterestingCategories = {
                ...newInterestingCategories,
                [uncategorizedCategory]: true
            };
        } else if(clearUncategorized) {
            newInterestingCategories = {
                ...newInterestingCategories,
                [uncategorizedCategory]: false
            };
        } else {
            newInterestingCategories = {
                ...newInterestingCategories,
                [uncategorizedCategory]: defaultInterestingCategories[uncategorizedCategory]
            };
        }
        setInterestingCategories(newInterestingCategories);
    };

    useEffect(() => {
        setEditedCategories(categories);
    }, [categories, isOpen]);

    const editedCategoriesExcludingUncategorized = useMemo(() => {
        if(!editedCategories) {
            return null;
        }
        return editedCategories.filter(c => c.uri !== uncategorizedCategory);
    });

    if (!accounts || !categories || !editedCategories) {
        return null;
    }

    const categoriesByUri = Object.fromEntries(editedCategories.map(category => ([category.uri, category])));
    const accountsByUri = Object.fromEntries(accounts.map(account => ([account.uri, account])));

    const renderContent = () => {
        if (!interestingCategories) {
            return null;
        }

        return (
            <div className={styles.categoryPicker}>
                <p>{bulkEditButtonText}</p>
                <table className={styles.editCategoriesTransactionTable}>
                    <thead>
                        <tr>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {editingTransactions.map(t =>
                            <Transaction
                                transaction={t}
                                showAccountName={true}
                                showCategories={false}
                                accountsByUri={accountsByUri}
                                key={t.uri}
                            />
                        )}
                    </tbody>
                </table>
                <h4>Categories</h4>
                <div className={styles.transactionCategoriesContainer}>
                    {Object
                        .entries(interestingCategories)
                        .map(([c, checked]) => ([categoriesByUri[c], checked]))
                        .toSorted((f, s) => f[0].name.localeCompare(s[0].name))
                        .map(([c, checked]) =>
                            <Category
                                category={c}
                                checked={checked}
                                onCheckedChange={
                                    c.uri !== uncategorizedCategory
                                    ? checked =>
                                        setInterestingCategoriesWithDefault({
                                            ...interestingCategories,
                                            [c.uri]: checked
                                        })
                                    : null
                                }
                                className={styles.category}
                                key={c.uri}
                            />
                        )}
                </div>
                <h4>Add more...</h4>
                <CategoriesEditor
                    showSearchFilter={true}
                    categories={editedCategoriesExcludingUncategorized}
                    checkedCategories={interestingCategories}
                    onCategoriesChanged={c => setEditedCategories(Object.values(c))}
                    onCheckedCategoriesChanged={c => setInterestingCategoriesWithDefault(c)}
                    className={styles.categoriesEditor}
                />
            </div>
        );
    };

    const buttons = [{
        text: "Cancel",
        onClick: onClose
    }];

    if (!!commonTransactionDescription) {
        buttons.push({
            text: "Save & Create Rule",
            onClick: () => onSave(
                interestingCategories,
                editedCategories, {
                    descriptionMatch: commonTransactionDescription,
                    merchantMatch: null,
                    categories: Object.entries(interestingCategories).filter(([_, c]) => c).map(([c, _]) => c)
                }
            )
        });
    }

    buttons.push({
        text: "Save",
        primary: true,
        onClick: () => onSave(interestingCategories, editedCategories, null)
    });

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={() => onClose()}
            style={mediumModal}
        >
            <ModalLayout
                title="Categorize transaction"
                buttons={buttons}
            >
                {renderContent()}
            </ModalLayout>
        </Modal>
    );
};

const MagicSearch = ({ onSearchFired }) => {
    const [ searchText, setSearchText ] = useState("");
    const previousText = usePrevious(searchText);
    const debounceTime = () => {
        if(!previousText) {
            return 0;
        } else if(searchText === "" && previousText !== "") {
            return 0;
        } else {
            return 200;
        }
    };

    const debouncedText = useDebounce(searchText, debounceTime());
    const firedText = useRef(searchText);
    const [ searching, setSearching ] = useState(false);
    const doSearch = async (text) => {
        if(text === firedText.current) {
            return;
        }
        setSearching(true);
        firedText.current = text;
        await onSearchFired(text);
        if(text === firedText.current) {
            setSearching(false);
        }
    };

    useEffect(() => {
        doSearch(debouncedText);
    }, [ debouncedText ]);

    return (
        <div className={styles.magicSearch}>
            <div className={styles.magicSearchControls}>
                <div className={styles.searchInputs}>
                    <div className={styles.searchContainer}>
                        <input
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            placeholder="Search..."
                            className={styles.search}
                        />
                        {searchText !== "" &&
                            <i
                                onClick={() => setSearchText("")}
                                className={cx("material-icons", styles.clear)}
                            >clear</i>
                        }
                    </div>
                    <button
                        onClick={() => doSearch(searchText)}
                        disabled={searching || searchText === firedText.current}
                    >
                        { searching
                            ? <i className={cx("material-symbols-outlined", styles.searchSpinner)}>progress_activity</i>
                            : <i className="material-icons">search</i>
                        }
                    </button>
                </div>
            </div>
        </div>
    );
};

const amountToString = amount => amount?.toFixed(2);
const stringToAmount = string => {
    const amount = Math.floor(parseFloat(string) * 100) / 100;
    return isNaN(amount) ? null : amount;
};

const AmountInput = ({ value, forceSign, onChange, disabled }) => {
    const [ stringValue, setStringValue ] = useState(amountToString(value));
    useEffect(() => {
        if (value !== null && value !== undefined) {
            const canonicalLocalStringValue = amountToString(stringToAmount(stringValue));
            const potentialNewStringValue = amountToString(value);
            if(canonicalLocalStringValue !== potentialNewStringValue) {
                setStringValue(potentialNewStringValue);
            }
        }
    }, [value]);
    return (
        <input
            type="number"
            value={stringValue}
            onChange={e => {
                let newStringValue = e.target.value;
                if(forceSign === -1 && !newStringValue.startsWith("-")) {
                    newStringValue = `-${newStringValue}`;
                }
                if(forceSign === 1 && newStringValue.startsWith("-")) {
                    newStringValue = newStringValue.substring(1);
                }
                const periodIndex = newStringValue.indexOf(".");
                if (periodIndex !== -1 && newStringValue.length - periodIndex > 3) {
                    // Limit to 2 decimal places
                    newStringValue = newStringValue.substring(0, periodIndex + 3);
                }
                const newAmount = stringToAmount(newStringValue);

                setStringValue(newStringValue);
                if (!isNaN(newAmount)) {
                    onChange(newAmount);
                } else {
                    onChange(null);
                }
            }}
            disabled={disabled}
        />
    );
}

const EditCategoriesOfSplitModal = ({
    isOpen,
    splitDescription,
    initialSelectedCategories,
    defaultCategories,
    uncategorizedCategory,
    onClose,
    onSave
}) => {
    const [editedCategories, setEditedCategories] = useState(null);
    const [selectedCategoryUris, setSelectedCategoryUris] = useState([]);

    const setSelectedCategoriesWithUncategorized = newSelectedUris => {
        newSelectedUris = newSelectedUris.filter(uri => uri !== uncategorizedCategory);
        if(newSelectedUris.length === 0) {
            newSelectedUris = [uncategorizedCategory];
        }
        setSelectedCategoryUris(newSelectedUris);
    };

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setEditedCategories(defaultCategories);
        setSelectedCategoriesWithUncategorized(initialSelectedCategories);
    }, [isOpen]);

    // Convert simple array to object format needed by CategoriesEditor
    const checkedCategoriesForEditor = useMemo(() =>
        Object.fromEntries(selectedCategoryUris.map(categoryUri => [categoryUri, true])),
        [selectedCategoryUris]
    );

    const editedCategoriesExcludingUncategorized = useMemo(() => 
        editedCategories?.filter(c => c.uri !== uncategorizedCategory),
        [editedCategories, uncategorizedCategory]
    );

    const handleCategoryToggle = (categoryUri, checked) => {
        let newSelectedUris = selectedCategoryUris;

        if (checked) {
            if (!newSelectedUris.includes(categoryUri)) {
                newSelectedUris = [...newSelectedUris, categoryUri];
            }
        } else {
            newSelectedUris = newSelectedUris.filter(uri => uri !== categoryUri);
        }

        setSelectedCategoriesWithUncategorized(newSelectedUris);
    };

    if (!isOpen || !editedCategories) {
        return null;
    }

    const categoriesByUri = Object.fromEntries(editedCategories.map(category => ([category.uri, category])));

    const renderContent = () => (
        <div className={styles.categoryPicker}>
            <h4>Categories</h4>
            <div className={styles.transactionCategoriesContainer}>
                {selectedCategoryUris
                    .map(categoryUri => categoriesByUri[categoryUri])
                    .filter(category => category)
                    .toSorted((a, b) => a.name.localeCompare(b.name))
                    .map(category =>
                        <Category
                            category={category}
                            checked={true}
                            onCheckedChange={
                                category.uri !== uncategorizedCategory
                                ? () => handleCategoryToggle(category.uri, false)
                                : null
                            }
                            className={styles.category}
                            key={category.uri}
                        />
                    )}
            </div>
            <h4>Add more...</h4>
            <CategoriesEditor
                showSearchFilter={true}
                categories={editedCategoriesExcludingUncategorized}
                checkedCategories={checkedCategoriesForEditor}
                onCategoriesChanged={updatedCategories => {
                    const uncategorizedCategoryObj = editedCategories.find(c => c.uri === uncategorizedCategory);
                    setEditedCategories([...updatedCategories, uncategorizedCategoryObj]);
                }}
                onCheckedCategoriesChanged={newCheckedCategories => {
                    let newSelectedUris = Object.entries(newCheckedCategories)
                        .filter(([_, checked]) => checked === true)
                        .map(([categoryUri, _]) => categoryUri);
                    setSelectedCategoriesWithUncategorized(newSelectedUris);
                }}
                className={styles.categoriesEditor}
            />
        </div>
    );

    const buttons = [{
        text: "Cancel",
        onClick: onClose
    }, {
        text: "Save",
        primary: true,
        onClick: () => {
            onSave(selectedCategoryUris, editedCategories);
        }
    }];

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            style={mediumModal}
        >
            <ModalLayout
                title={`Edit Categories: ${splitDescription}`}
                buttons={buttons}
            >
                {renderContent()}
            </ModalLayout>
        </Modal>
    );
};

const SplitTransactionModal = ({ 
    transaction, 
    categories, 
    accounts,
    spaceScopedFetch,
    uncategorizedCategory,
    onClose, 
    onSave
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [splits, setSplits] = useState([]);
    const [editingSplitIndex, setEditingSplitIndex] = useState(null);
    const [editedCategories, setEditedCategories] = useState(null);

    const isOpen = !!transaction;

    useEffect(() => {
        if(!isOpen) {
            return;
        }

        setEditedCategories(categories);

        if (transaction.hasBeenSplit) {
            // Load existing splits from the API
            setIsLoading(true);
            fetchTransactionSplit(transaction.uri, spaceScopedFetch)
                .then(response => {
                    setSplits(response.splits);
                    setIsLoading(false);
                });
        } else {
            // Create default split - single row with full amount
            setSplits([{
                amount: transaction.amount, 
                description: transaction.description, 
                categories: transaction.categories,
                locked: false
            }]);
        }
    }, [isOpen]);

    const addSplit = () => {
        setSplits([
            ...splits, { 
                amount: 0, 
                description: `${splits.length + 1} ${transaction.description}`,
                categories: transaction.categories,
                locked: false
            }
        ])
    };

    const distributeToOtherSplits = (amount, splits, except) => {
        const isOtherSplit = (s, i) => !s.locked && i !== except;
        const otherAmount = splits.reduce((sum, split, index) => sum + (isOtherSplit(split, index) ? (split.amount ?? 0) : 0), 0);

        return splits.map((split, index) => {
            if (!isOtherSplit(split, index) || split.amount === null) {
                return split;
            }
            const newAmount = split.amount * (1 + 1 / otherAmount * amount);
            return { ...split, amount: newAmount };
        })
    }

    const removeSplit = index => {
        const split = splits[index];
        var newSplits = splits.filter((_, i) => i !== index);
        if(split.amount !== null) {
            newSplits = distributeToOtherSplits(
                split.amount,
                newSplits,
                null
            )
        }
        setSplits(newSplits);
    };

    const updateSplit = (index, delta) => {
        setSplits(splits.toSpliced(index, 1, {
            ...splits[index],
            ...delta
        }));
    }

    if (!isOpen || !editedCategories) {
        return null;
    }

    const categoriesByUri = Object.fromEntries(editedCategories.map(category => ([category.uri, category])));
    const accountsByUri = Object.fromEntries(accounts.map(account => ([account.uri, account])));
    const currency = accountsByUri[transaction.account].currency;

    const unlockedCount = splits.filter(split => !split.locked).length;

    const renderSplitRow = (split, index) => (
        <tr key={index}>
            <td>
                <button
                    className={cx("material-icons", styles.lockButton, { [styles.locked]: split.locked })}
                    onClick={() => updateSplit(index, { locked: !split.locked })}
                    title={split.locked ? "Unlock" : "Lock"}
                >
                    {split.locked ? "lock" : "lock_open"}
                </button>
            </td>
            <td>
                <AmountInput
                    value={split.amount}
                    onChange={value => {
                        const split = splits[index];
                        setSplits(distributeToOtherSplits(
                            (split.amount ?? 0) - (value ?? 0),
                            splits.toSpliced(index, 1, { ...split, amount: value }),
                            index
                        ));
                    }}
                    forceSign={transaction?.amount < 0 ? -1 : 1}
                    disabled={split.locked || (unlockedCount === 1 && !split.locked)}
                />
            </td>
            <td>
                <input
                    type="text"
                    value={split.description || ''}
                    onChange={e => updateSplit(index, { description: e.target.value })}
                    style={{ width: '200px' }}
                />
            </td>
            <td>
                <div 
                    className={styles.transactionCategoriesContainer}
                    onClick={() => setEditingSplitIndex(index)}
                    style={{ cursor: 'pointer' }}
                >
                    {split.categories.map(categoryUri => {
                        const category = categoriesByUri[categoryUri];
                        return category ? (
                            <Category
                                key={categoryUri}
                                category={category}
                                checked={true}
                            />
                        ) : null;
                    })}
                </div>
            </td>
            <td>
                {splits.length > 1 && (
                    <button
                        type="button"
                        className="material-icons"
                        onClick={() => removeSplit(index)}
                    >
                        delete
                    </button>
                )}
            </td>
        </tr>
    );

    const buttons = [{
        text: "Cancel",
        onClick: onClose
    }, {
        text: "Save",
        disabled: isLoading || splits.length < 2 || splits.some(split => split.amount === null),
        primary: true,
        onClick: () => {
            onSave(splits, editedCategories);
        }
    }];

    if (transaction.hasBeenSplit) {
        buttons.splice(1, 0, {
            text: "Delete Split",
            danger: true,
            onClick: () => onSave(null, editedCategories)
        });
    }

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            style={largeModal}
        >
            <ModalLayout
                title={`Split Transaction: ${transaction.description}`}
                buttons={buttons}
            >
                <div className={styles.splitContainer}>
                    <p>Original Amount: {formatMoney(transaction.amount, currency)}</p>
                    <button
                        onClick={addSplit}
                    >
                        Create new split
                    </button>
                    <table className={styles.splitTable}>
                        <thead>
                            <tr>
                                <th>Lock</th>
                                <th>Amount</th>
                                <th>Description</th>
                                <th>Categories</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {splits.map((split, index) => renderSplitRow(split, index))}
                        </tbody>
                    </table>
                </div>
            </ModalLayout>
            <EditCategoriesOfSplitModal
                isOpen={editingSplitIndex !== null}
                splitDescription={splits[editingSplitIndex]?.description}
                initialSelectedCategories={splits[editingSplitIndex]?.categories}
                defaultCategories={editedCategories}
                uncategorizedCategory={uncategorizedCategory}
                onClose={() => setEditingSplitIndex(null)}
                onSave={(selectedCategories, newEditCategories) => {
                    setEditedCategories(newEditCategories);
                    setSplits(currentSplits =>
                        currentSplits.map((split, index) =>
                            index === editingSplitIndex
                                ? { ...split, categories: selectedCategories }
                                : split
                        )
                    );
                    setEditingSplitIndex(null);
                }}
            />
        </Modal>
    );
};

const TransactionSummaryPanel = ({ filters, spaceScopedFetch }) => {
    const { data: summaryData, isLoading } = useQuery({
        queryKey: ['transaction-summary', spaceScopedFetch?.ident, filters],
        queryFn: () => fetchTransactionSummary(filters, spaceScopedFetch),
        enabled: !!spaceScopedFetch
    });

    if (isLoading || !summaryData) {
        return (
            <div className={styles.summaryPanel}>
                <div className={styles.summaryContent}>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.summaryPanel}>
            <div className={styles.summaryContent}>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Total Incoming</span>
                    <span className={styles.summaryValue}>
                        {formatMoney(summaryData.total_incoming, summaryData.currency)}
                    </span>
                </div>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Total Outgoing</span>
                    <span className={styles.summaryValue}>
                        {formatMoney(summaryData.total_outgoing, summaryData.currency)}
                    </span>
                </div>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Number of Transactions</span>
                    <span className={styles.summaryValue}>
                        {summaryData.transaction_count}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default () => {
    const {
        accounts,
        activeAccounts,
        identity,
        categoriesQuery: { data: categories, refetch: refetchCategories },
        currentSpace,
        spaceScopedFetch,
        userScopedFetch,
        agentFilter,
        setAgentFilter
    } = useAppContext();

    const seenSearch = useRef(null);
    const [localFilter, setLocalFilter] = useState({ search: "" });
    const effectiveFilter = agentFilter ?? localFilter;
    const filtersWithExternalProps = useMemo(() => {
        return {
            ...effectiveFilter,
            activeAccounts
        };
    }, [effectiveFilter, activeAccounts]);

    const [transactions, loadingTransactions, loadNextPage, loadPreviousPage, invalidateTransactions] = useTransactions(
        filtersWithExternalProps, spaceScopedFetch
    );

    const transactionsByUri = useMemo(() => Object.fromEntries(transactions.map(t => [t.uri, t])), [transactions]);

    const [selectedTransactions, setSelectedTransactions] = useState([]);
    const [unselectedTransactions, setUnselectedTransactions] = useState([]);
    const [allTransactionsSelected, setAllTransactionsSelected] = useState(false);
    const [commonTransactionDescription, setCommonTransactionDescription] = useState(null);

    useEffect(() => {
        setSelectedTransactions([]);
        setUnselectedTransactions([]);
        setAllTransactionsSelected(false);
    }, [effectiveFilter]);

    const previousCategories = useRef(null);
    useEffect(() => {
        const wasLoaded = previousCategories.current !== null;
        const hasChanged = previousCategories.current !== categories;
        if (wasLoaded && hasChanged) {
            invalidateTransactions();
        }
        previousCategories.current = categories;
    }, [categories]);

    const [interestingCategories, setInterestingCategories] = useState(null);
    const [creatingNewRule, setCreatingNewRule] = useState(null);
    const [splittingTransaction, setSplittingTransaction] = useState(null);

    const handleSplitTransaction = async (splits, editedCategories) => {
        if (!splittingTransaction) return;

        try {
            // First, save any edited categories (created, modified, or deleted) during split editing
            if (editedCategories && editedCategories !== categories) {
                const categoriesWithUris = await saveCategories(editedCategories, useTransactions);
                const newUrisMap = Object.fromEntries(editedCategories.map((c, i) => ([c.uri, categoriesWithUris[i].uri])));
                
                // Update category URIs in splits if any were using temporary URIs
                if (splits) {
                    splits = splits.map(split => ({
                        ...split,
                        categories: split.categories.map(catUri => newUrisMap[catUri] || catUri)
                    }));
                }
                
                // Refresh categories
                await refetchCategories();
            }
            
            if (splits === null) {
                // Delete split
                await deleteTransactionSplit(splittingTransaction.uri, spaceScopedFetch);
            } else {
                // Create or update split
                await saveTransactionSplit(splittingTransaction.uri, { splits }, spaceScopedFetch);
            }
            
            await invalidateTransactions();
            setSplittingTransaction(null);
        } catch (error) {
            console.error('Error splitting transaction:', error);
            // You might want to show an error message to the user here
        }
    };

    const renderTransactionsList = () => {
        if (!accounts || !categories) {
            return null;
        }
        const accountsByUri = Object.fromEntries(accounts.map(account => ([account.uri, account])));
        const categoriesByUri = Object.fromEntries(categories.map(category => ([category.uri, category])));

        return transactions.filter(t => !t.hasBeenSplit).map(t =>
            <Transaction
                transaction={t}
                checked={
                    (allTransactionsSelected && !unselectedTransactions.includes(t.uri))
                    || selectedTransactions.includes(t.uri)
                }
                accountsByUri={accountsByUri}
                categoriesByUri={categoriesByUri}
                showAccountName={activeAccounts.length !== 1}
                showCategories={true}
                onClickCategories={() => {
                    setSelectedTransactions([t.uri]);
                    setInterestingCategories(Object.fromEntries(t.categories.map(c => [c, true])));
                }}
                onSplitClick={transaction => setSplittingTransaction(transactionsByUri[transaction.splitFrom] || transaction)}
                onCheckedChange={checked => {
                    if (allTransactionsSelected) {
                        let result = unselectedTransactions.filter(s => s !== t.uri);
                        if (!checked) {
                            result = [...result, t.uri];
                        }
                        setUnselectedTransactions(result);
                    } else {
                        let result = selectedTransactions.filter(s => s !== t.uri);
                        if (checked) {
                            result = [...result, t.uri];
                        }
                        setSelectedTransactions(result);
                    }
                }}
                key={t.uri}
            />
        );
    };

    let bulkEditButtonText = null;
    if (allTransactionsSelected) {
        const exception = unselectedTransactions.length > 0 ? `but ${unselectedTransactions.length} ` : ``;
        bulkEditButtonText = `Edit all ${exception}transactions`;
    } else if (selectedTransactions.length > 0) {
        bulkEditButtonText = `Edit ${selectedTransactions.length} transactions`;
    }

    const loadDataOnScroll = scrollingElement => {
        const threshold = 200;
        const topScrollPosition = scrollingElement.scrollTop;
        const bottomScrollPosition = scrollingElement.scrollTop + scrollingElement.clientHeight;
        if(topScrollPosition < threshold) {
            loadPreviousPage?.();
        }
        if (bottomScrollPosition >= scrollingElement.scrollHeight - threshold) {
            loadNextPage?.();
        }
    };

    return (
        <div className={styles.root}>
            <h1><BackArrow /><span className="material-symbols-outlined">wallet</span><span>Transactions</span></h1>
            <div className={styles.actions}>
                <IndeterminateCheckbox
                    checked={unselectedTransactions.length > 0 ? null : allTransactionsSelected}
                    onChange={e => {
                        setAllTransactionsSelected(e.target.checked);
                        setSelectedTransactions([]);
                        setUnselectedTransactions([]);
                    }}
                />
                {agentFilter
                    ? <div className={styles.agentFilterBanner}>
                        <span className="material-icons">smart_toy</span>
                        <span>AI filter active</span>
                        <button
                            onClick={() => {
                                setAgentFilter(null);
                                setLocalFilter({ search: "" });
                            }}
                            className={cx("material-icons", styles.agentFilterClear)}
                            title="Clear AI filter"
                        >clear</button>
                      </div>
                    : <MagicSearch
                        onSearchFired={async searchText => {
                            seenSearch.current = searchText;
                            setLocalFilter({ search: searchText });
                        }}
                      />
                }
                <button
                    onClick={() => {
                        downloadTransactions(
                            filtersWithExternalProps, spaceScopedFetch
                        );
                    }}
                    className="material-icons"
                >download</button>
                {bulkEditButtonText &&
                    <button
                        className={cx("material-icons", styles.bulkEditButton)}
                        onClick={async () => {
                            if (allTransactionsSelected) {
                                const interestingCategoriesResult = await getBulkTransactionCategories(
                                    filtersWithExternalProps, {}, spaceScopedFetch
                                );
                                setInterestingCategories(Object.fromEntries([
                                    ...interestingCategoriesResult.universal.map(c => [c, true]),
                                    ...interestingCategoriesResult.partial.map(c => [c, null])
                                ]));
                                setCommonTransactionDescription(
                                    interestingCategoriesResult.commonDescription
                                );
                            } else {
                                const selectedTransactionObjects = selectedTransactions.map(t => transactionsByUri[t]);
                                const descriptions = [... new Set(selectedTransactionObjects.map(t => t.description))];
                                setCommonTransactionDescription(descriptions.length === 1 ? descriptions[0] : null);
                                const allSelectedCategories = [
                                    ...new Set(selectedTransactionObjects.flatMap(t => t.categories))
                                ];
                                const newInterestingCategories = Object
                                    .fromEntries(allSelectedCategories
                                        .map(category => ([
                                            category,
                                            selectedTransactionObjects.every(
                                                transaction => transaction.categories.includes(category)
                                            ) ? true : null
                                        ]))
                                    );
                                setInterestingCategories(newInterestingCategories);
                            }
                        }}
                    >edit</button>
                }
            </div>
            <div
                onScrollCapture={event => loadDataOnScroll(event.target) }
                className={styles.tableScroller}
            >
                <table>
                    <thead>
                        <tr>
                            <th></th>
                            <th></th>
                            <th></th>
                            <th></th>
                            {activeAccounts.length !== 1 && <th></th>}
                            <th></th>
                            <th></th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {renderTransactionsList()}
                    </tbody>
                </table>
                {transactions.length === 0 && !loadingTransactions &&
                    <p className={styles.endOfTransactionsLabel}>No transactions found</p>
                }
            </div>
            <TransactionSummaryPanel 
                filters={filtersWithExternalProps}
                spaceScopedFetch={spaceScopedFetch}
            />
            <EditCategoriesModal
                bulkEditButtonText={bulkEditButtonText}
                editingTransactions={selectedTransactions.map(t => transactionsByUri[t])}
                commonTransactionDescription={commonTransactionDescription}
                defaultInterestingCategories={interestingCategories}
                accounts={accounts}
                categories={categories}
                uncategorizedCategory={currentSpace?.uncategorizedCategory}
                onClose={() => setInterestingCategories(null)}
                onSave={async (interestingCategories, newCategories, potentialNewRule) => {
                    const categoriesWithUris = await saveCategories(newCategories, spaceScopedFetch);
                    const newUrisMap = Object.fromEntries(newCategories.map((c, i) => ([ c.uri, categoriesWithUris[i].uri ])));

                    const interestingCategoriesWithUris = Object.fromEntries(
                        Object.entries(interestingCategories)
                            .map(([c, checked]) => ([newUrisMap[c], checked]))
                    );

                    await bulkSaveTransactionCategories(
                        allTransactionsSelected,
                        allTransactionsSelected ? filtersWithExternalProps : null,
                        allTransactionsSelected ? unselectedTransactions : selectedTransactions,
                        Object.entries(interestingCategoriesWithUris).filter(([_, c]) => c === true).map(([c, _]) => c),
                        Object.entries(interestingCategoriesWithUris).filter(([_, c]) => c === false).map(([c, _]) => c),
                        spaceScopedFetch
                    );
                    await invalidateTransactions();
                    setInterestingCategories(null);
                    if(potentialNewRule) {
                        potentialNewRule.categories = potentialNewRule.categories.map(c => newUrisMap[c]);
                    }
                    setCreatingNewRule(potentialNewRule);
                    await refetchCategories();
                }}
            />
            <EditRuleModal
                isOpen={!!creatingNewRule}
                defaultRule={creatingNewRule}
                categories={categories}
                onClose={() => setCreatingNewRule(null)}
                onSave={async (newRule, apply) => {
                    await saveRule(newRule, apply, null, spaceScopedFetch);
                    invalidateTransactions();
                    setCreatingNewRule(null);
                }}
            />
            <SplitTransactionModal
                transaction={splittingTransaction}
                categories={categories}
                accounts={accounts}
                spaceScopedFetch={spaceScopedFetch}
                uncategorizedCategory={currentSpace?.uncategorizedCategory}
                onClose={() => {
                    setSplittingTransaction(null);
                }}
                onSave={handleSplitTransaction}
            />
        </div>
    );
};
